/**
 * Words for the desk's status line and position strip (owner 2026-10-04 UI clean-up: every
 * non-trading value, status or settings entry appears once). Pure, so the labels are unit-tested;
 * the React parts live in apps/web/src/topbar.
 *
 * The status line's one connection item reads "Rithmic ● Connected / Connecting / Disconnected /
 * Rejected": the browser↔gateway socket first (nothing behind it is live while it is down), then
 * the Rithmic login's order plant. Its menu carries the one-line detail the old connection banner
 * showed ("Reconnecting — attempt 21").
 */
import { MINUS } from "./format.ts";

export type ConnectionTone = "connected" | "connecting" | "error";

export type ConnectionItem = {
  tone: ConnectionTone;
  /** "Connected", "Connecting", "Reconnecting", "Disconnected", "Rejected", "Logged out", "Degraded". */
  label: string;
  /** One line for the menu: "Reconnecting — attempt 21", "Order plant ready"… */
  detail: string;
  /** The gateway is not connected after data arrived: what is on screen is the last known state. */
  stale: boolean;
};

/** The subset of the trading-socket connection the item needs. */
export type ConnectionInput = {
  status: "disconnected" | "connecting" | "connected";
  reconnectAttempt: number;
  lastError: string | null;
  /** Set after the first Welcome (data on screen may be stale once the socket drops). */
  sessionId: string | null;
};

/** The Rithmic login line (`rithmicLoginLine` in @helm/core): state and its words. */
export type LoginInput = {
  state: "connected" | "connecting" | "error" | "off";
  stateText: string;
};

/** The order plant's own state words, when a session is known ("ready", "syncing"…). */
export type PlantInput = { ready: boolean; words: string; detail: string } | null;

export function connectionItem(c: ConnectionInput, login: LoginInput, plant: PlantInput): ConnectionItem {
  const hadData = c.sessionId !== null;
  const err = c.lastError ? ` (${c.lastError})` : "";
  if (c.status === "connecting") {
    if (c.reconnectAttempt > 0)
      return {
        tone: "connecting",
        label: "Reconnecting",
        detail: `Reconnecting — attempt ${String(c.reconnectAttempt + 1)}${err}`,
        stale: hadData,
      };
    return {
      tone: "connecting",
      label: "Connecting",
      detail: hadData ? "Reconnecting to the gateway" : "Connecting to the gateway",
      stale: hadData,
    };
  }
  if (c.status === "disconnected")
    return {
      tone: "error",
      label: "Disconnected",
      detail: `Disconnected from the gateway${err}`,
      stale: hadData,
    };
  // Gateway up: the Rithmic login decides.
  const words = login.stateText.replace(/…$/, "");
  switch (login.state) {
    case "connected":
      return {
        tone: "connected",
        label: "Connected",
        detail:
          plant === null || plant.ready
            ? "Order plant ready"
            : `Order plant ${plant.words}${plant.detail ? ` — ${plant.detail}` : ""}`,
        stale: false,
      };
    case "connecting":
      return { tone: "connecting", label: "Connecting", detail: "Logging in to Rithmic", stale: false };
    case "error":
      return {
        tone: "error",
        label: words,
        detail:
          plant !== null && plant.detail
            ? `${words} — ${plant.detail}`
            : `${words}: trading is disabled until the login reconnects`,
        stale: false,
      };
    default:
      return {
        tone: "error",
        label: "Disconnected",
        detail: "Gateway connected — no Rithmic login yet. Trading is disabled.",
        stale: false,
      };
  }
}

/** "+3 @ 21,450.25", "−2 @ 6,663.75", "Flat"; "+3 · 2 accounts" when the average would mix accounts. */
export function positionText(netQty: number, avg: string | null, accounts = 1): string {
  if (netQty === 0) return "Flat";
  const qty = `${netQty > 0 ? "+" : MINUS}${String(Math.abs(netQty))}`;
  if (accounts > 1) return `${qty} · ${String(accounts)} accounts`;
  return avg === null || avg === "—" ? qty : `${qty} @ ${avg}`;
}

export type SessionPhase = "rth" | "eth" | "closed";

/** "CME · RTH open", "CME · ETH open", "Closed" (the venue only while a market is open). */
export function sessionTag(venue: string | null, phase: SessionPhase): string {
  const state = phase === "rth" ? "RTH open" : phase === "eth" ? "ETH open" : "Closed";
  return venue ? `${venue} · ${state}` : state;
}

/** The status line's one-click switch: always shown, so it names both states. */
export function oneClickStatusLabel(on: boolean): "1-click on" | "1-click off" {
  return on ? "1-click on" : "1-click off";
}

/**
 * The trading account, in the same words wherever it is picked (status line and each panel's
 * picker): there is ONE selection, every panel trades it, and picking it anywhere changes it
 * everywhere. "All accounts" shows totals only; orders need one account.
 */
export const TRADING_ACCOUNT = "Trading account";
/** The panel picker's menu heading (the status line's menu note says the same in full). */
export const TRADING_ACCOUNT_SHARED = "Trading account for every panel";
export const TRADING_ACCOUNT_NOTE =
  "The trading account for every panel. Picking an account in any panel changes it here too.";
export const ALL_ACCOUNTS_NOTE = "All accounts shows totals; pick one account to trade.";
/** A panel picker's placeholder while "All accounts" is selected (short: it leads a 240 px row). */
export const PICK_ACCOUNT = "Pick account";

/** Browser↔gateway round trip: "18 ms", or "— ms" before the first measurement. */
export function latencyText(ms: number | null): string {
  return ms === null ? "— ms" : `${String(Math.round(ms))} ms`;
}

/** Futures root of a contract: "NQZ6" → "NQ", "MESH27" → "MES"; a root stays itself. */
export function contractRoot(symbol: string): string {
  return symbol.replace(/[FGHJKMNQUVXZ]\d{1,2}$/, "");
}

/** Exchange of a root, for the session tag (the status line never watches md for it). */
const VENUES: Record<string, string> = {
  NQ: "CME",
  MNQ: "CME",
  ES: "CME",
  MES: "CME",
  RTY: "CME",
  M2K: "CME",
  "6E": "CME",
  "6J": "CME",
  YM: "CBOT",
  MYM: "CBOT",
  ZB: "CBOT",
  ZN: "CBOT",
  ZF: "CBOT",
  ZC: "CBOT",
  CL: "NYMEX",
  MCL: "NYMEX",
  NG: "NYMEX",
  GC: "COMEX",
  MGC: "COMEX",
  SI: "COMEX",
  HG: "COMEX",
};

/** "NQZ6" / "NQ" → "CME"; null for a symbol the table does not know. */
export function venueOf(symbol: string | null): string | null {
  if (!symbol) return null;
  return VENUES[contractRoot(symbol)] ?? null;
}
